import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Stack,
  alpha,
  LinearProgress,
  Collapse,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  People as PeopleIcon,
  AdminPanelSettings as RolesIcon,
  Webhook as IntegrationsIcon,
  Description as TemplatesIcon,
  Warehouse as WarehouseIcon,
  History as AuditIcon,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  PauseCircle,
  Notifications as NotificationsIcon,
  PendingActions as PendingIcon,
  Bolt as WorkerIcon,
  LinearScale as QueueIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as RunIcon,
  ListAlt as HistoryIcon,
  BugReport as FailureIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterIcon,
  CheckCircleOutline,
  WarningAmber,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';

interface BackgroundJobsOverview {
  enabled: boolean;
  online?: boolean;
  message?: string;
  workers: Array<{ name: string; active_tasks: number; scheduled_tasks: number; reserved_tasks: number; pool?: string }>;
  active_tasks: number;
  scheduled_tasks: number;
  reserved_tasks: number;
}

interface ScheduledJobRunRecord {
  id: string;
  job_name: string;
  status: 'success' | 'failed' | string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  result_summary: string | null;
  error_message: string | null;
}

interface JobStatItem {
  name: string;
  label: string;
  frequency: string;
  description: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  failure_rate_percent: number;
  avg_duration_ms: number | null;
}

interface ScheduledJobRunsResponse {
  summary: Record<string, ScheduledJobRunRecord | null>;
  recent: ScheduledJobRunRecord[];
  total_count?: number;
  stats?: {
    total_runs: number;
    total_failed: number;
    total_success: number;
    overall_failure_rate: number;
    jobs: Record<string, JobStatItem>;
  };
  table_ready: boolean;
}

interface BackgroundJobItem {
  id: string;
  type: string;
  type_label: string;
  name: string;
  status: string;
  progress_percent: number;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_by: string | null;
}

const SCHEDULED_JOB_CONFIG: Record<string, { label: string; frequency: string; description: string }> = {
  cleanup_expired_stock_reservations: {
    label: 'Stock Reservation Cleanup',
    frequency: 'Every 5m',
    description: 'Releases held inventory for expired checkout reservations',
  },
  notify_abandoned_carts: {
    label: 'Abandoned Cart Nudges',
    frequency: 'Every 30m',
    description: 'Sends WhatsApp/Email reminders for abandoned carts',
  },
  cleanup_expired_carts: {
    label: 'Expired Carts Cleanup',
    frequency: 'Every 1h',
    description: 'Cleans up abandoned carts older than retention threshold',
  },
  check_low_stock_alerts: {
    label: 'Low Stock Alerts',
    frequency: 'Every 6h',
    description: 'Evaluates reorder thresholds and dispatches stock alerts',
  },
  update_sales_trends_cache: {
    label: 'Sales Trends Cache',
    frequency: 'Daily (24h)',
    description: 'Pre-aggregates variant velocity & sales trend charts',
  },
  calculate_customer_aging: {
    label: 'Customer Aging Calculation',
    frequency: 'Daily (24h)',
    description: 'Computes outstanding overdue balances and credit aging tiers',
  },
  cleanup_audit_logs: {
    label: 'Audit Log Cleanup',
    frequency: 'Weekly (7d)',
    description: 'Archives audit trail records older than 90 days',
  },
};

const JOB_STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  pending: 'default',
  RUNNING: 'info',
  in_progress: 'info',
  PAUSED: 'warning',
  COMPLETED: 'success',
  completed: 'success',
  FAILED: 'error',
  failed: 'error',
  success: 'success',
};

const ACTIVE_JOB_STATUSES = new Set(['DRAFT', 'RUNNING', 'pending', 'in_progress']);

interface ITSummary {
  integration_health: {
    total_services: number;
    active_services: number;
    error_services: number;
    inactive_services: number;
    services: Array<{ service_type: string; provider_name: string; status: string; message?: string }>;
  };
  recent_notification_failures: Array<{
    channel: string | null;
    provider_name: string | null;
    error_message: string | null;
    failed_at: string | null;
    event_type: string | null;
  }>;
  pending_template_approvals: number;
  admin_users_count: number;
  roles_count: number;
}

interface ShortcutCard {
  label: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  permission: string;
  color: string;
}

const ITDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ITSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [jobsOverview, setJobsOverview] = useState<BackgroundJobsOverview | null>(null);
  const [jobs, setJobs] = useState<BackgroundJobItem[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [scheduledRuns, setScheduledRuns] = useState<ScheduledJobRunsResponse | null>(null);
  const [scheduledRunsLoading, setScheduledRunsLoading] = useState(true);
  const [expandedJobError, setExpandedJobError] = useState<string | null>(null);

  // Background Jobs Sub-navigation & Filtering
  const [jobViewTab, setJobViewTab] = useState<'overview' | 'history' | 'async_jobs'>('overview');
  const [filterJobName, setFilterJobName] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [runningJobKey, setRunningJobKey] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/dashboard/it-summary');
      setSummary(response.data);
    } catch (err: any) {
      setError(
        err?.response?.status === 403
          ? "You don't have permission to view the IT dashboard."
          : 'Failed to load system status. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBackgroundJobs = useCallback(async () => {
    try {
      const [overviewRes, jobsRes] = await Promise.all([
        api.get('/admin/background-jobs/overview'),
        api.get('/admin/background-jobs', { params: { limit: 25 } }),
      ]);
      setJobsOverview(overviewRes.data);
      setJobs(jobsRes.data?.items || []);
    } catch (err) {
      // Background jobs panel is best-effort
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const fetchScheduledRuns = useCallback(async () => {
    setScheduledRunsLoading(true);
    try {
      const params: any = { limit: 50 };
      if (filterJobName !== 'all') params.job_name = filterJobName;
      if (filterStatus !== 'all') params.status = filterStatus.toUpperCase();
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await api.get('/admin/background-jobs/scheduled-runs', { params });
      setScheduledRuns(res.data);
    } catch {
      // best-effort
    } finally {
      setScheduledRunsLoading(false);
    }
  }, [filterJobName, filterStatus, searchQuery]);

  const handleRunJobNow = async (jobName: string) => {
    setRunningJobKey(jobName);
    try {
      const res = await api.post('/admin/background-jobs/run-job', { job_name: jobName });
      if (res.data.success) {
        enqueueSnackbar(res.data.message || `Job '${jobName}' executed successfully`, { variant: 'success' });
      } else {
        enqueueSnackbar(res.data.message || `Job '${jobName}' completed with error`, { variant: 'error' });
      }
      // Refresh execution history
      await fetchScheduledRuns();
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to trigger job';
      enqueueSnackbar(`Trigger error: ${msg}`, { variant: 'error' });
    } finally {
      setRunningJobKey(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied traceback to clipboard', { variant: 'info' });
  };

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchBackgroundJobs(); }, [fetchBackgroundJobs]);
  useEffect(() => { fetchScheduledRuns(); }, [fetchScheduledRuns]);

  // Periodic polling while any background job is active
  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => ACTIVE_JOB_STATUSES.has(j.status));
    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      fetchBackgroundJobs();
    }, 4000);

    return () => clearInterval(interval);
  }, [jobs, fetchBackgroundJobs]);

  const shortcuts: ShortcutCard[] = [
    { label: 'Users', description: 'Manage admin accounts', icon: <PeopleIcon />, path: '/admin/users', permission: 'admin.users.read', color: '#1976d2' },
    { label: 'Roles', description: 'Manage roles & permissions', icon: <RolesIcon />, path: '/admin/roles', permission: 'admin.roles.read', color: '#8e24aa' },
    { label: 'Integrations', description: 'Configure providers', icon: <IntegrationsIcon />, path: '/admin/integrations/config', permission: 'integrations.read', color: '#0288d1' },
    { label: 'Templates', description: 'Document & notification templates', icon: <TemplatesIcon />, path: '/admin/integrations/templates', permission: 'integrations.read', color: '#2e7d32' },
    { label: 'Warehouses', description: 'System configuration', icon: <WarehouseIcon />, path: '/admin/configuration/warehouses', permission: 'admin.system.config', color: '#ed6c02' },
    { label: 'Audit Logs', description: 'Review admin activity', icon: <AuditIcon />, path: '/analytics/audit-logs', permission: 'admin.audit.read', color: '#5d4037' },
  ].filter((s) => hasPermission(s.permission));

  const statusIcon = (status: string) => {
    if (status === 'active') return <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />;
    if (status === 'error') return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    return <PauseCircle sx={{ color: 'text.disabled', fontSize: 18 }} />;
  };

  const totalRuns = scheduledRuns?.stats?.total_runs || 0;
  const totalFailed = scheduledRuns?.stats?.total_failed || 0;
  const failureRate = scheduledRuns?.stats?.overall_failure_rate || 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <PageHeader title="IT & Diagnostics Hub" subtitle="Worker diagnostics, scheduled background tasks, and real-time execution logs" />
        <Tooltip title="Refresh All Data">
          <IconButton onClick={() => { fetchSummary(); fetchBackgroundJobs(); fetchScheduledRuns(); }} color="primary" disabled={loading}
            sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={fetchSummary}>Retry</Button>
        }>
          {error}
        </Alert>
      )}

      {/* Shortcut cards */}
      {shortcuts.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {shortcuts.map((s) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={s.label}>
              <Card
                onClick={() => navigate(s.path)}
                sx={{
                  cursor: 'pointer', height: '100%',
                  border: `1px solid ${alpha(s.color, 0.15)}`,
                  boxShadow: `0 2px 12px ${alpha(s.color, 0.08)}`,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${alpha(s.color, 0.18)}` },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%', mx: 'auto', mb: 1.5,
                    bgcolor: alpha(s.color, 0.12), color: s.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {s.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>{s.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Integration health + pending approvals */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Integration Health</Typography>
              {loading ? (
                <Skeleton variant="rounded" height={140} />
              ) : summary ? (
                <>
                  <Stack direction="row" spacing={3} mb={2}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {summary.integration_health.active_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Active</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {summary.integration_health.error_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Error</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.disabled">
                        {summary.integration_health.inactive_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Inactive</Typography>
                    </Box>
                  </Stack>
                  {summary.integration_health.services.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.integration_health.services.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{s.service_type}</TableCell>
                              <TableCell>{s.provider_name}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  {statusIcon(s.status)}
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{s.status}</Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No integrations configured yet.</Typography>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            onClick={() => hasPermission('integrations.read') && navigate('/admin/integrations/templates')}
            sx={{
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider',
              height: '100%', cursor: hasPermission('integrations.read') ? 'pointer' : 'default',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PendingIcon color="warning" />
                <Typography variant="h6" fontWeight={700}>Pending Approvals</Typography>
              </Stack>
              {loading ? (
                <Skeleton variant="text" width={80} height={60} />
              ) : (
                <>
                  <Typography variant="h3" fontWeight={800} color={summary && summary.pending_template_approvals > 0 ? 'warning.main' : 'text.primary'}>
                    {summary?.pending_template_approvals ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Notification templates awaiting approval</Typography>
                </>
              )}
              <Box mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? <Skeleton width={140} /> : `${summary?.admin_users_count ?? 0} active admin users`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {loading ? <Skeleton width={100} /> : `${summary?.roles_count ?? 0} roles configured`}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* BACKGROUND JOBS & WORKERS SECTION */}
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid', borderColor: 'divider', mb: 3 }}>
        {/* Header with Executive KPI Counters */}
        <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha('#1976d2', 0.02) }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <WorkerIcon color="primary" sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight={800}>Background Jobs & Scheduler Control</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Monitor periodic automation tasks, diagnose failed executions, and trigger on-demand jobs
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Executions</Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.main">{totalRuns.toLocaleString()}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Failures Logged</Typography>
                  <Typography variant="h6" fontWeight={800} color={totalFailed > 0 ? 'error.main' : 'success.main'}>
                    {totalFailed.toLocaleString()} {totalRuns > 0 && `(${failureRate}%)`}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Worker Runtime</Typography>
                  <Typography variant="subtitle2" fontWeight={700} color={jobsOverview?.online ? 'success.main' : 'text.primary'}>
                    {jobsOverview?.enabled && jobsOverview?.online ? 'Celery Cluster' : 'APScheduler Async'}
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
          </Grid>

          {/* Navigation Tabs */}
          <Box sx={{ mt: 2.5 }}>
            <Tabs
              value={jobViewTab}
              onChange={(_, val) => setJobViewTab(val)}
              textColor="primary"
              indicatorColor="primary"
              sx={{ minHeight: 40 }}
            >
              <Tab
                value="overview"
                icon={<ScheduleIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Periodic Jobs (7 Configured)"
                sx={{ fontWeight: 700, minHeight: 40, textTransform: 'none' }}
              />
              <Tab
                value="history"
                icon={<FailureIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Execution & Failure Logs (${scheduledRuns?.recent?.length || 0})`}
                sx={{ fontWeight: 700, minHeight: 40, textTransform: 'none' }}
              />
              <Tab
                value="async_jobs"
                icon={<HistoryIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Campaigns & Async Tasks (${jobs.length})`}
                sx={{ fontWeight: 700, minHeight: 40, textTransform: 'none' }}
              />
            </Tabs>
          </Box>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          {/* TAB 1: PERIODIC SCHEDULED JOBS OVERVIEW */}
          {jobViewTab === 'overview' && (
            <Box>
              {scheduledRunsLoading ? (
                <Skeleton variant="rounded" height={260} />
              ) : !scheduledRuns?.table_ready ? (
                <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                  Scheduled job history table is not ready yet — run <code>alembic upgrade head</code> on the server to create it.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: alpha('#1976d2', 0.04) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Job & Frequency</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Purpose / Scope</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Health Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Lifetime Stats</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Execution</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last Duration</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(SCHEDULED_JOB_CONFIG).map(([key, config]) => {
                        const run = scheduledRuns?.summary?.[key] ?? null;
                        const jobStat = scheduledRuns?.stats?.jobs?.[key];
                        const isRunning = runningJobKey === key;
                        const isExpanded = expandedJobError === key;

                        return (
                          <React.Fragment key={key}>
                            <TableRow hover>
                              <TableCell sx={{ py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={700}>{config.label}</Typography>
                                <Chip label={config.frequency} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem', height: 20, mt: 0.5 }} />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 280 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {config.description}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {run ? (
                                  <Chip
                                    label={run.status.toUpperCase()}
                                    size="small"
                                    color={run.status.toLowerCase() === 'success' ? 'success' : 'error'}
                                    icon={run.status.toLowerCase() === 'success' ? <CheckCircleOutline /> : <ErrorIcon />}
                                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                  />
                                ) : (
                                  <Chip label="NEVER RUN" size="small" color="default" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell>
                                {jobStat ? (
                                  <Box>
                                    <Typography variant="caption" fontWeight={600} display="block">
                                      {jobStat.total_runs.toLocaleString()} runs
                                    </Typography>
                                    {jobStat.failed_runs > 0 ? (
                                      <Typography variant="caption" color="error.main" fontWeight={700}>
                                        {jobStat.failed_runs} failed ({jobStat.failure_rate_percent}%)
                                      </Typography>
                                    ) : (
                                      <Typography variant="caption" color="success.main" fontWeight={600}>
                                        100% success
                                      </Typography>
                                    )}
                                  </Box>
                                ) : '—'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" fontWeight={500} display="block">
                                  {run?.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {run?.duration_ms != null
                                    ? run.duration_ms >= 1000
                                      ? `${(run.duration_ms / 1000).toFixed(2)}s`
                                      : `${run.duration_ms}ms`
                                    : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                  {run?.error_message && (
                                    <Tooltip title={isExpanded ? 'Hide Traceback' : 'View Error Details'}>
                                      <IconButton size="small" color="error" onClick={() => setExpandedJobError(isExpanded ? null : key)}>
                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <FailureIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <RunIcon />}
                                    disabled={isRunning}
                                    onClick={() => handleRunJobNow(key)}
                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
                                  >
                                    {isRunning ? 'Running...' : 'Run Now'}
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                            {/* Expandable Error Traceback */}
                            {run?.error_message && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0, px: 2 }}>
                                  <Collapse in={isExpanded}>
                                    <Box
                                      sx={{
                                        my: 1.5,
                                        p: 2,
                                        bgcolor: alpha('#d32f2f', 0.05),
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: alpha('#d32f2f', 0.25),
                                      }}
                                    >
                                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2" color="error.main" fontWeight={700}>
                                          Error Traceback & Execution Failure Log
                                        </Typography>
                                        <Button
                                          size="small"
                                          startIcon={<CopyIcon fontSize="small" />}
                                          onClick={() => copyToClipboard(run.error_message || '')}
                                          sx={{ textTransform: 'none', color: 'error.main' }}
                                        >
                                          Copy Traceback
                                        </Button>
                                      </Stack>
                                      <Typography variant="caption" color="error.dark" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', display: 'block' }}>
                                        {run.error_message}
                                      </Typography>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 2: DETAILED EXECUTION & FAILURE LOGS WITH FILTERING */}
          {jobViewTab === 'history' && (
            <Box>
              {/* Filter Controls Bar */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: alpha('#1976d2', 0.02) }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Filter by Job"
                      value={filterJobName}
                      onChange={(e) => setFilterJobName(e.target.value)}
                    >
                      <MenuItem value="all">All Scheduled Jobs</MenuItem>
                      {Object.entries(SCHEDULED_JOB_CONFIG).map(([k, cfg]) => (
                        <MenuItem key={k} value={k}>{cfg.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3} md={2.5}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Status"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="FAILED">Failed Only ⚠️</MenuItem>
                      <MenuItem value="SUCCESS">Success Only ✓</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={5} md={4.5}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search error messages or results..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchScheduledRuns()}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<FilterIcon />}
                      onClick={fetchScheduledRuns}
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Filter
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Logs Table */}
              {scheduledRunsLoading ? (
                <Skeleton variant="rounded" height={300} />
              ) : scheduledRuns?.recent?.length === 0 ? (
                <Alert severity="info" sx={{ my: 2 }}>
                  No execution records matched your filter criteria.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: alpha('#1976d2', 0.04) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Execution Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Job Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Result Summary / Error</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduledRuns?.recent.map((r) => {
                        const isExpanded = expandedJobError === r.id;
                        const isFailed = r.status.toLowerCase() === 'failed';
                        const jobConfig = SCHEDULED_JOB_CONFIG[r.job_name] || { label: r.job_name };

                        return (
                          <React.Fragment key={r.id}>
                            <TableRow hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="caption" fontWeight={600}>
                                  {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{jobConfig.label}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {r.job_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={r.status.toUpperCase()}
                                  size="small"
                                  color={isFailed ? 'error' : 'success'}
                                  variant="filled"
                                  sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {r.duration_ms != null
                                    ? r.duration_ms >= 1000
                                      ? `${(r.duration_ms / 1000).toFixed(2)}s`
                                      : `${r.duration_ms}ms`
                                    : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 360 }}>
                                {isFailed ? (
                                  <Typography variant="caption" color="error.main" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                    {r.error_message || 'Error occurred during execution'}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                    {r.result_summary || 'Completed successfully'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => setExpandedJobError(isExpanded ? null : r.id)}>
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                            </TableRow>

                            {/* Collapsible Full Diagnostic Row */}
                            <TableRow>
                              <TableCell colSpan={6} sx={{ py: 0, px: 2 }}>
                                <Collapse in={isExpanded}>
                                  <Box
                                    sx={{
                                      my: 1.5,
                                      p: 2,
                                      bgcolor: isFailed ? alpha('#d32f2f', 0.05) : alpha('#2e7d32', 0.05),
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: isFailed ? alpha('#d32f2f', 0.25) : alpha('#2e7d32', 0.25),
                                    }}
                                  >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Typography variant="subtitle2" fontWeight={700} color={isFailed ? 'error.main' : 'success.main'}>
                                        {isFailed ? 'Execution Traceback & Failure Diagnostic' : 'Result Payload & Summary'}
                                      </Typography>
                                      {r.error_message && (
                                        <Button
                                          size="small"
                                          startIcon={<CopyIcon fontSize="small" />}
                                          onClick={() => copyToClipboard(r.error_message || '')}
                                          sx={{ textTransform: 'none', color: 'error.main' }}
                                        >
                                          Copy Error
                                        </Button>
                                      )}
                                    </Stack>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        display: 'block',
                                        color: isFailed ? 'error.dark' : 'text.primary',
                                      }}
                                    >
                                      {isFailed ? r.error_message : (r.result_summary || 'No payload recorded')}
                                    </Typography>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* TAB 3: ASYNC CAMPAIGNS & EXPORTS */}
          {jobViewTab === 'async_jobs' && (
            <Box>
              {jobsLoading ? (
                <Skeleton variant="rounded" height={220} />
              ) : jobs.length === 0 ? (
                <Alert severity="info">No recent campaign or export jobs found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: alpha('#1976d2', 0.04) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Task Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Job Name / Identifier</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Started At</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Completed At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobs.map((j) => (
                        <TableRow key={`${j.type}-${j.id}`} hover>
                          <TableCell>
                            <Chip label={j.type_label} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {j.name}
                          </TableCell>
                          <TableCell>
                            <Chip label={j.status} size="small" color={JOB_STATUS_COLOR[j.status] || 'default'} variant="filled" sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LinearProgress
                                variant="determinate"
                                value={j.progress_percent}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="caption" fontWeight={600}>{Math.round(j.progress_percent)}%</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {j.started_at ? new Date(j.started_at).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {j.completed_at ? new Date(j.completed_at).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Recent notification failures */}
      <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <NotificationsIcon color="error" />
            <Typography variant="h6" fontWeight={700}>Recent Notification Failures</Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rounded" height={160} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Failed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!summary || summary.recent_notification_failures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No recent failures</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.recent_notification_failures.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip label={f.channel || 'unknown'} size="small" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(f.event_type || '').toLowerCase().replace(/_/g, ' ')}</TableCell>
                        <TableCell>{f.provider_name || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.error_message || '-'}
                        </TableCell>
                        <TableCell>{f.failed_at ? new Date(f.failed_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ITDashboard;

